/**
 * useModalBackdrop - click-outside-to-close for a dialog, with no click
 * handler on the dialog box itself.
 *
 * Every dialog used to stop clicks on its box (`onClick={e =>
 * e.stopPropagation()}`) so they would not reach the backdrop's close
 * handler. That is a click handler on a non-interactive element, which
 * jsx-a11y rightly reads as a control nobody can reach by keyboard. This
 * moves the decision to the backdrop, keeping the behaviour exactly:
 *
 * - a click that started inside the box is STOPPED at the backdrop, as
 *   the box used to stop it: it neither closes the dialog nor reaches
 *   anything above the backdrop;
 * - any other click - the backdrop itself, or something drawn beside the
 *   box, like a hidden file input - closes, with the event, and carries on
 *   propagating as before.
 *
 * "Inside" is DOM containment. A portal rendered from inside a dialog
 * would count as outside, and React bubbles its clicks here: a portal that
 * opens from a dialog stops its own clicks (the library picker, from the
 * terrain editor and the character library, 2026-10-20).
 *
 * @param onClose - called with the click event; absent means the backdrop
 *   does not close.
 * @param trapRef - the focus trap's ref, filled with the box as well, so
 *   one `ref={boxRef}` serves both.
 * @returns `onBackdropClick` for the backdrop and `boxRef` for the box.
 */
import { useCallback, useRef } from 'react';
import type { MouseEvent, MutableRefObject } from 'react';

export default function useModalBackdrop(
  onClose: ((e: MouseEvent) => void) | null | undefined,
  trapRef?: MutableRefObject<HTMLElement | null> | null,
) {
  const box = useRef<HTMLElement | null>(null);
  const boxRef = useCallback(
    (node: HTMLElement | null) => {
      box.current = node;
      if (trapRef) trapRef.current = node;
    },
    [trapRef],
  );
  const onBackdropClick = useCallback(
    (e: MouseEvent) => {
      if (box.current?.contains(e.target as Node)) {
        e.stopPropagation();
        return;
      }
      onClose?.(e);
    },
    [onClose],
  );
  return { onBackdropClick, boxRef };
}
